import type { MouseEvent } from "react"
import { ArrowUp, X } from "lucide-react"
import { cn } from "../lib/utils"

/**
 * The control a chat file shows while it goes, as WhatsApp draws it: a ring
 * that fills with the bytes sent and an X in the middle to stop it.
 *
 * `progress` is 0..1. Null, or 1, spins instead of filling: every byte can be
 * gone while the server is still storing the file and the message has not been
 * sent, and a full ring sitting still there read as stuck.
 *
 * `tone`: "media" sits on a photo or video (white on a dark disc), "plain"
 * takes the colour of the text around it (a document card, a voice note).
 */
export function UploadRing({
  progress,
  onCancel,
  size = 44,
  tone = "media",
  className,
}: {
  progress: number | null
  /** Absent once the upload can no longer be stopped: the ring spins, no X. */
  onCancel?: () => void
  size?: number
  tone?: "media" | "plain"
  className?: string
}) {
  const stroke = Math.max(2, Math.round(size / 16))
  const r = (size - stroke) / 2 - 1
  const circumference = 2 * Math.PI * r
  const filling = progress !== null && progress < 1
  // Never quite empty, so the ring is visibly there from the first moment.
  const shown = filling ? Math.max(0.04, progress) : 0.25
  const Tag = onCancel ? "button" : "div"

  return (
    <Tag
      {...(onCancel
        ? {
            type: "button" as const,
            "aria-label": "Cancel upload",
            onClick: (e: MouseEvent) => {
              e.stopPropagation()
              onCancel()
            },
          }
        : { role: "progressbar", "aria-label": "Sending" })}
      {...(filling ? { "aria-valuenow": Math.round(progress * 100), "aria-valuemin": 0, "aria-valuemax": 100 } : {})}
      style={{ width: size, height: size }}
      className={cn(
        "relative grid shrink-0 place-items-center rounded-full",
        tone === "media" ? "bg-black/45 text-white backdrop-blur-sm" : "text-current",
        onCancel && "cursor-pointer transition-transform active:scale-95",
        className,
      )}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className={cn("absolute inset-0", !filling && "animate-spin")}
        aria-hidden
      >
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeOpacity={0.25} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - shown)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: filling ? "stroke-dashoffset 200ms linear" : undefined }}
        />
      </svg>
      {onCancel && <X className="relative" style={{ width: size * 0.38, height: size * 0.38 }} strokeWidth={2.5} />}
    </Tag>
  )
}

/** In the ring's place once an upload was stopped or failed: ↑, tap to send
 *  it again — WhatsApp's mark for an outgoing file that has not gone. */
export function UploadRetry({
  onRetry,
  size = 44,
  tone = "media",
  className,
}: {
  onRetry: () => void
  size?: number
  tone?: "media" | "plain"
  className?: string
}) {
  return (
    <button
      type="button"
      aria-label="Send again"
      onClick={(e) => {
        e.stopPropagation()
        onRetry()
      }}
      style={{ width: size, height: size }}
      className={cn(
        "grid shrink-0 place-items-center rounded-full transition-transform active:scale-95",
        tone === "media" ? "bg-black/45 text-white backdrop-blur-sm" : "border-2 border-current text-current",
        className,
      )}
    >
      <ArrowUp style={{ width: size * 0.46, height: size * 0.46 }} strokeWidth={2.5} />
    </button>
  )
}
